import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Tooltip, Button } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { mediaPickerActions } from '../store';
import UploadIcon from '@mui/icons-material/CloudUploadRounded';
import { ASSET_UPLOADING_MODE, MEDIA_RESOURCE_TYPE, MEDIA_SOURCE_TYPE, MEDIA_USAGE_TYPE, IMAGE_UPLOAD_RESTRICTION } from '../../CreatePageConstants';
import { createActions } from '../../store';
import { dataURLtoBlob } from '../../../../../utilities/fileUtilities';
import { createMediaSource, createMediaSourceDataForVideo } from '../../helpers';
import AppConstants from '../../../../../App/AppConstants';
import MediaPickersConstants from '../MediaPickersConstants';
import { appOperations } from '../../../../../App/store';
import ColoredSnackbarConstants from '../../../../misc/ColoredSnackbar/ColoredSnackbarConstants';

const styles = theme => ({
    input: { display: 'none' },
    icon: { marginRight: theme.spacing(1) },
    button: { marginRight: theme.spacing(1) }
});

export let UploadMediaInputReference;

const UploadMediaButton = ({
    classes, title, recommendedSize, showImageCropper, mediaFormat,
    mediaUsageType, uploadMimeTypes, onImageSelected, updateBackgroundSettings, setMediaPickerProperty,
    openPremiumVideosDialog, subscriptionType, openSnack, savingInProgress,
    showCriticalOperationProgressWarning, isVideoExportsQueued, setCreateProperty,
    directUploadEnabled, uploadBackgroundVideoDirectly 
}) => {
    const [fileValidating, setFileValidating] = useState(false);

    const showWarning = (message) => {
        openSnack(message, ColoredSnackbarConstants.SNACKBAR_WARNING);
    };

    const applyBackgroundVideo = async (file, dataURL) => {
        const { uploadedFile, cancelled } = await uploadBackgroundVideoDirectly (file);
        if (cancelled)
            return;

        const activeUrl = uploadedFile ? uploadedFile.url : URL.createObjectURL(dataURL ? dataURLtoBlob(dataURL) : file);
        const mediaData = createMediaSourceDataForVideo(activeUrl, activeUrl, activeUrl, activeUrl);
        const mediaSource = createMediaSource(mediaData, MEDIA_SOURCE_TYPE.UPLOADED, null, MEDIA_RESOURCE_TYPE.VIDEO);
        if (uploadedFile)
            mediaSource.directUpload = uploadedFile;

        updateBackgroundSettings({ mediaSource });
    };

    const handleVideoUpload = (file, input, dataURL) => {
        const video = document.createElement('video');
        video.preload = 'metadata';

        video.onloadedmetadata = () => {
            URL.revokeObjectURL(video.src);

            const { videoWidth: width, videoHeight: height, duration } = video;
            setCreateProperty('backgroundDimensions', { width, height });

            if (width < IMAGE_UPLOAD_RESTRICTION.MIN_WIDTH || height < IMAGE_UPLOAD_RESTRICTION.MIN_HEIGHT) {
                showWarning(`The video resolution is too low. Minimum is ${IMAGE_UPLOAD_RESTRICTION.MIN_WIDTH}x${IMAGE_UPLOAD_RESTRICTION.MIN_HEIGHT}.`);
                input.value = '';
                return;
            }

            if (width > IMAGE_UPLOAD_RESTRICTION.MAX_WIDTH || height > IMAGE_UPLOAD_RESTRICTION.MAX_HEIGHT) {
                showWarning('The video resolution must not exceed 8K.');
                input.value = '';
                return;
            }

            if (duration < 1) {
                showWarning('Video is too short. Please use a video that is at least 1 second long.');
                input.value = '';
                return;
            }

            if (mediaUsageType === MEDIA_USAGE_TYPE.LOGO) {
                setMediaPickerProperty('logoMediaPickerOpen', false);
            } else if (mediaUsageType === MEDIA_USAGE_TYPE.BACKGROUND) {
                if (subscriptionType === AppConstants.FREE_SUBSCRIPTION_TYPE) {
                    const localUrl = URL.createObjectURL(dataURL ? dataURLtoBlob(dataURL) : file);
                    setMediaPickerProperty('backgroundMediaPickerOpen', false);
                    openPremiumVideosDialog(localUrl, () => {
                        setMediaPickerProperty('backgroundMediaPickerOpen', true);
                    }, 'uploading videos');
                } else {
                    setMediaPickerProperty('backgroundMediaPickerOpen', false);
                    applyBackgroundVideo(file, dataURL);
                }
            }
            input.value = '';
        };

        video.src = URL.createObjectURL(file);
    };

    const handleImageUpload = (input, dataURL) => {
        setFileValidating(true);
        const img = new Image();

        img.onload = () => {
            const { width, height } = img;
            setCreateProperty('backgroundDimensions', { width, height });

            if (width < IMAGE_UPLOAD_RESTRICTION.MIN_WIDTH || height < IMAGE_UPLOAD_RESTRICTION.MIN_HEIGHT) {
                showWarning(`The image resolution is too low. Minimum is ${IMAGE_UPLOAD_RESTRICTION.MIN_WIDTH}x${IMAGE_UPLOAD_RESTRICTION.MIN_HEIGHT}.`);
                input.value = '';
                setFileValidating(false);
                return;
            }

            if (width > IMAGE_UPLOAD_RESTRICTION.MAX_WIDTH || height > IMAGE_UPLOAD_RESTRICTION.MAX_HEIGHT) {
                showWarning('The image resolution must not exceed 8K.');
                input.value = '';
                setFileValidating(false);
                return;
            }

            // Pickers that take the image as is, with no cropping step, handle the file themselves.
            if (onImageSelected)
                onImageSelected(dataURL);
            else
                showImageCropper(dataURL, mediaFormat, MEDIA_SOURCE_TYPE.UPLOADED, mediaUsageType);

            setFileValidating(false);
        };

        img.src = dataURL;
    };

    const onUploadMediaClick = () => {
        if (savingInProgress) {
            showCriticalOperationProgressWarning();
            return;
        }
        if (isVideoExportsQueued()) return;
        UploadMediaInputReference?.click();
    };

    const onInputChange = (event) => {
        const input = event.target;
        if (!(input.files && input.files[0])) return;

        const file = input.files[0];
        const fileSizeMB = file.size / (1024 * 1024);

        if (MediaPickersConstants.MAX_UPLOAD_VIDEO_SIZE < fileSizeMB) {
            showWarning(`The video file limit is ${MediaPickersConstants.MAX_UPLOAD_VIDEO_SIZE} mb`);
            input.value = '';
            return;
        }

        if (file.type === 'video/mp4' && directUploadEnabled) {
            handleVideoUpload(file, input, null);
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const dataURL = e.target.result;
            switch (file.type) {
                case 'video/mp4':
                    handleVideoUpload(file, input, dataURL);
                    break;
                case 'image/jpeg':
                case 'image/png':
                case 'image/gif':
                    handleImageUpload(input, dataURL);
                    break;
                default:
                    showWarning('File extension is not supported.');
            }
        };
        reader.readAsDataURL(file);
    };

    return (
        <>
            <input
                type='file'
                accept={uploadMimeTypes}
                onChange={onInputChange}
                className={classes.input}
                ref={(input) => (UploadMediaInputReference = input)}
            />
            <Tooltip
                // describeChild keeps the button's own label as its accessible name; without it MUI
                // moves the tooltip text into aria-label and the button stops being findable by name.
                describeChild
                title={recommendedSize ? `Recommended media size: ${recommendedSize}` : 'Upload an image from your device'}
                placement='top'
                arrow
            >
                <Button
                    className={classes.button}
                    size='large'
                    variant='outlined'
                    color='inherit'
                    onClick={onUploadMediaClick}
                    disabled={fileValidating}
                    startIcon={<UploadIcon className={classes.icon} />}
                >
                    {title}
                </Button>
            </Tooltip>
        </>
    );
};

const mapStateToProps = (state) => {
    const { subscriptionType } = state.app.accountDetails;
    const { savingInProgress } = state.create.videoSavingState;
    const directUploadEnabled = state.create.assetUploadingMode === ASSET_UPLOADING_MODE.DIRECT;
    return { subscriptionType, savingInProgress, directUploadEnabled };
}

const mapDispatchToProps = (dispatch) => {
    const setCreateProperty = (key, value) => dispatch(createActions.setProperty(key, value));
    const setMediaPickerProperty = (key, value) => dispatch(mediaPickerActions.setProperty(key, value));
    const isVideoExportsQueued = () => dispatch(createActions.isVideoExportsQueued());
    const showImageCropper = (data, format, mediaSourceType, mediaUsageType, mediaResourceId, mediaResourceUrl) => dispatch(mediaPickerActions.showImageCropper(data, format, mediaSourceType, mediaUsageType, mediaResourceId, mediaResourceUrl));
    const updateBackgroundSettings = (properties) => dispatch(createActions.updateBackgroundSettings(properties));
    const openPremiumVideosDialog = (videoUrl, onGoBack, location) => dispatch(mediaPickerActions.openPremiumVideosDialog(videoUrl, onGoBack, location));
    const openSnack = (text, variant) => dispatch(appOperations.openSnack(text, variant));
    const showCriticalOperationProgressWarning = () => dispatch(createActions.showCriticalOperationProgressWarning());
    const uploadBackgroundVideoDirectly  = (file) => dispatch(createActions.uploadBackgroundVideoDirectly (file));

    return {
        setMediaPickerProperty,
        showImageCropper,
        updateBackgroundSettings,
        openPremiumVideosDialog,
        openSnack,
        isVideoExportsQueued,
        setCreateProperty,
        showCriticalOperationProgressWarning,
        uploadBackgroundVideoDirectly 
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(UploadMediaButton, styles));